import AppAlert from '@nxsys/components/AppAlert';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import BankSelectInput from '@nxsys/components/BankSelectInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { verifyBankAccount } from '@nxsys/services/api/bank';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import React, { Ref, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';

export type BankDetailValues = {
  bankName: Option<string> | null;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  buildingSocietyNumber: string;
  IBANReference: string;
  SWIFTNo: string;
  bankCountry: Option<string> | null;
  isInternationalAccount: boolean;
};

export type BankDetailFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => BankDetailValues;
  resetFields: () => void;
};

type BankDetailFormProps = {
  formRef?: Ref<BankDetailFormRef>;
  initialValues?: BankDetailValues;
};

const BankDetailForm: React.FC<BankDetailFormProps> = ({
  formRef,
  initialValues,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm<BankDetailValues>();
  // The Ireland account type keeps optional fields visible, but only account
  // name and IBAN are mandatory for an Irish worker.
  const isInternationalAccount = Form.useWatch('isInternationalAccount', form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const [verifyBankLoading, setVerifyBankLoading] = useState(false);
  const [verifyBankResult, setVerifyBankResult] = useState<{
    status: 'success' | 'warning';
    message: string;
  } | null>(null);

  const handleVerifyBank = async () => {
    const accountNumber = form.getFieldValue('accountNumber');
    const sortCode = form.getFieldValue('sortCode');
    if (!accountNumber || !sortCode) {
      showMessage({ type: 'warning', content: messages['form.pleaseEnter'] });
      return;
    }
    try {
      setVerifyBankLoading(true);
      verifyBankAccount({
        params: { AccountNumbers: accountNumber, SortCodes: sortCode },
        cb: (data: any) => {
          if (!data) return;
          if (!data?.Error) {
            setVerifyBankResult({
              status: 'success',
              message: messages['common.verifyBankSuccess'],
            });
          } else {
            const { Cause, Description, Resolution } = data;
            setVerifyBankResult({
              status: 'warning',
              message: `${Cause} ${Description}. ${Resolution}`,
            });
          }
          setVerifyBankLoading(false);
        },
      });
    } catch (err) {
      console.error(err);
      setVerifyBankLoading(false);
    }
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="text-[18px] font-bold mb-[40px]">
        {messages['common.bankDetails'] ?? 'Bank Details'}
      </div>

      {verifyBankResult && (
        <div className="mb-4">
          <AppAlert
            type={verifyBankResult.status}
            variant={verifyBankResult.status}
            message={verifyBankResult.message}
          />
        </div>
      )}

      {isIreland && (
        <div className="mb-4 flex items-center gap-2">
          <Form.Item
            name="isInternationalAccount"
            valuePropName="checked"
            noStyle
          >
            <AppSwitch
              // The switch only moves which fields are required, so the rules
              // have to be re-run against the values already entered.
              onChange={() => form.validateFields()}
            />
          </Form.Item>
          <span>
            {messages['common.internationalAccount'] ?? 'International Account'}
          </span>
        </div>
      )}

      {/* Bank Name */}
      <Form.Item label={messages['common.bankName']} name="bankName">
        <BankSelectInput
          className="w-full !min-w-0"
          placeholder={messages['form.select']}
        />
      </Form.Item>

      {/* Account Name */}
      <Form.Item
        label={messages['common.accountName']}
        name="accountName"
        rules={[
          {
            required: true,
            whitespace: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder="e.g. John Smith" />
      </Form.Item>

      {/* Sort Code | Account Number */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.sortCode6Digits']}
            name="sortCode"
            rules={[
              ...(!isIreland && !isInternationalAccount
                ? [{ required: true, message: messages['form.pleaseEnter'] }]
                : []),
              {
                pattern: /^\d{6}$/,
                message: messages['form.sortCodeMustBe6Digits'],
              },
            ]}
            normalize={(value: string) => value.replace(/[^0-9]/g, '')}
          >
            <AppInput placeholder={messages['form.enter']} maxLength={6} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.accountNumber8Digits']}
            name="accountNumber"
            rules={[
              ...(!isIreland && !isInternationalAccount
                ? [{ required: true, message: messages['form.pleaseEnter'] }]
                : []),
              {
                pattern: /^\d{8}$/,
                message: messages['form.accountNumberMustBe8Digits'],
              },
            ]}
            normalize={(value: string) => value.replace(/[^0-9]/g, '')}
          >
            <AppInput placeholder={messages['form.enter']} maxLength={8} />
          </Form.Item>
        </Col>
      </Row>

      {/* Building Society Number */}
      <Form.Item
        label={messages['common.buildingSocietyNumberUpto24Digits']}
        name="buildingSocietyNumber"
        normalize={(value: string) => value.replace(/[^0-9]/g, '')}
      >
        <AppInput placeholder={messages['form.enter']} maxLength={24} />
      </Form.Item>

      {/* IBAN Reference | SWIFT No */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.IBANReferenceUpto30Digits']}
            name="IBANReference"
            rules={
              isIreland || isInternationalAccount
                ? [{ required: true, message: messages['form.pleaseEnter'] }]
                : []
            }
            normalize={(value: string) =>
              value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
            }
          >
            <AppInput placeholder={messages['form.enter']} maxLength={30} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.SWIFTNoUpto20Digits']}
            name="SWIFTNo"
            rules={
              !isIreland && isInternationalAccount
                ? [{ required: true, message: messages['form.pleaseEnter'] }]
                : []
            }
            normalize={(value: string) =>
              value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
            }
          >
            <AppInput placeholder={messages['form.enter']} maxLength={20} />
          </Form.Item>
        </Col>
      </Row>

      {/* Country */}
      <Form.Item
        label={messages['common.country']}
        name="bankCountry"
        rules={
          !isIreland && isInternationalAccount
            ? [{ required: true, message: messages['form.pleaseSelect'] }]
            : []
        }
      >
        <AppSelect
          options={countryList}
          className="w-full !min-w-0"
          placeholder={messages['form.selectCountry']}
        />
      </Form.Item>

      {/* Account verification is a UK-only service, as on the Bank Details tab. */}
      {!isIreland && (
        <AppButton
          variant="outlined"
          name={messages['common.verify']}
          onClick={handleVerifyBank}
          loading={verifyBankLoading}
          disabled={verifyBankLoading}
        />
      )}
    </AppFormContainer>
  );
};

export default BankDetailForm;
